import { ACCESS_GROUPS } from "../../access-control/models/access-groups.models";
import { saveThemePreference, type AppearanceTheme } from "../../appearance/user-prefs.service";
import { useAuthContext } from "../../auth/state/AuthContext";
import NotificationsDrawer from "../../notifications/components/NotificationsDrawer";
import { SupportContactModal } from "../../tech-form/components/SupportContactModal";
import { ChevronDownIcon } from "../Icons";
import { featureFlagsAtom, isDarkModeAtom, themeAtom } from "../atoms";
import { formatName } from "../services/utils";
import { LogoutConfirmationModal } from "./LogoutConfirmationModal";
import { Menu, Transition } from "@headlessui/react";
import { useAtom, useAtomValue } from "jotai";
import React, { Fragment, useCallback, useState } from "react";
import { NavLink } from "react-router-dom";
import { twMerge } from "tailwind-merge";

interface HeaderProps {
  user: {
    name: string;
    email: string;
    profileImage?: string;
    company?: { name: string; logoUrl: string };
  };
  navigation: {
    name: string;
    path?: string;
    callback?: () => void | Promise<void>;
    icon?: React.ReactNode;
    requiresConfirmation?: boolean;
  }[];
  leftComponent?: React.ReactNode;
  notification?: React.ReactNode; // TODO REVIEW if we need this param at all
}

const profileMenuItemClassName =
  "mb-2 flex w-full cursor-pointer items-center justify-start px-0 py-0 text-[14px] font-normal last:mb-0";
const profileMenuLinkClassName = "mb-2 block text-[14px] font-normal last:mb-0";
const profileMenuItemContentClassName = "flex items-center hover:text-primary-main";
const themeOptionClassName = "mb-2 flex cursor-pointer items-center gap-2 text-[13px] font-normal last:mb-0";
const themeRadioInputClassName = "sr-only";
const themeRadioIndicatorClassName =
  "flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full border bg-transparent";
const themeRadioDotClassName = "h-1.5 w-1.5 rounded-full";
const profileDetailLabelClassName = "mb-0.5 text-[11px] font-semibold uppercase opacity-70";
const profileDetailValueClassName = "break-words text-[13px]";
const profileAccessBadgeClassName = "rounded-full border px-2 py-0.5 text-[11px]";

const roleGroupLabels: Record<string, string> = {
  DXP_Opslink_RoleDeveloper: "Developer",
  DXP_Opslink_RoleTester: "Tester",
  DXP_Opslink_RoleVesselOperator: "Vessel Operator",
  DXP_Opslink_RoleVesselMaster: "Vessel Master",
  DXP_Opslink_RoleTradeExecutionSpecialist: "Trade Execution Specialist",
  DXP_Opslink_RoleOpsSupport: "Ops Support",
  DXP_Opslink_RoleSeniorVO: "Senior Vessel Operator",
  DXP_Opslink_RoleProcurement: "Procurement",
  DXP_Opslink_RoleVesselPerformance: "Vessel Performance",
  DXP_Opslink_RolePFL: "PFL",
  DXP_Opslink_RoleLaytime: "Laytime",
  DXP_Opslink_RoleTrader: "Trader",
  DXP_Opslink_RoleClaims: "Claims",
  DXP_Opslink_RoleLeadership: "Leadership",
  DXP_Opslink_RoleDigital: "Digital",
  DXP_Opslink_RoleShipowner: "Ship Owner",
};

const accessGroupLabels: Record<string, string> = {
  [ACCESS_GROUPS.InternalUsers]: "Internal user",
  [ACCESS_GROUPS.ExternalUsers]: "External user",
  [ACCESS_GROUPS.Admin]: "Admin",
};

const getGroupLabels = (groups: string[], groupLabels: Record<string, string>) =>
  Array.from(new Set(groups.flatMap((group) => (groupLabels[group] ? [groupLabels[group]] : []))));

const formatProfileValue = (values: string[]) => (values.length > 0 ? values.join(", ") : "Not assigned");

const CustomHeader = ({ leftComponent, user, notification, navigation }: HeaderProps) => {
  const theme = useAtomValue(themeAtom);
  const accessToken = useAuthContext().state.user?.accessToken;
  const [isDarkMode, setIsDarkMode] = useAtom(isDarkModeAtom);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isAppearanceMenuOpen, setIsAppearanceMenuOpen] = useState(false);
  const [isSupportContactModalOpen, setIsSupportContactModalOpen] = useState(false);
  const [logoutConfirmationCallback, setLogoutConfirmationCallback] = useState<(() => void | Promise<void>) | null>(
    null,
  );

  const nameArray = user.name?.split(" ");
  const firstNameFirstLetter = (nameArray?.length ?? 0) > 1 ? nameArray?.[0]?.charAt(0) : "";
  const lastNameFirstLetter = (nameArray?.length ?? 0) > 1 ? nameArray?.[1]?.charAt(0) : "";
  const featureFlags = useAtomValue(featureFlagsAtom);
  const appearanceTheme: AppearanceTheme = isDarkMode ? "night" : "bright";
  const profileMenuTextColor = theme.headerComponent.profileMenu.text;
  const profileMenuTextStyle = { color: profileMenuTextColor };
  const themeRadioBorderStyle = { borderColor: profileMenuTextColor };
  const themeRadioDotStyle = { backgroundColor: profileMenuTextColor };
  const profileAccessBadgeStyle = { borderColor: profileMenuTextColor, color: profileMenuTextColor };
  const tokenFullName = [accessToken?.firstname, accessToken?.lastname].filter(Boolean).join(" ").trim();
  const profileName = user.name?.trim() || tokenFullName || "Not available";
  const profileEmail = user.email?.trim() || accessToken?.sub || "Not available";
  const tokenGroups = accessToken?.groups ?? [];
  const profileRoleLabels = getGroupLabels(tokenGroups, roleGroupLabels);
  const profileAccessLabels = getGroupLabels(tokenGroups, accessGroupLabels);

  const handleThemeSelect = useCallback(
    (nextTheme: AppearanceTheme) => {
      setIsDarkMode(nextTheme === "night");
      saveThemePreference(nextTheme).catch((error) => {
        console.error("Failed to save theme preference", error);
      });
    },
    [setIsDarkMode],
  );

  const closeUserMenuSubmenus = useCallback(() => {
    setIsProfileMenuOpen(false);
    setIsAppearanceMenuOpen(false);
  }, []);

  const closeLogoutConfirmation = useCallback(() => {
    setLogoutConfirmationCallback(null);
  }, []);

  const confirmLogout = useCallback(() => {
    const callback = logoutConfirmationCallback;
    setLogoutConfirmationCallback(null);
    void callback?.();
  }, [logoutConfirmationCallback]);

  return (
    <>
      <header className="relative flex h-10 flex-shrink-0 items-center">
        <div className="hidden md:flex md:min-w-0 md:flex-1 md:items-center md:justify-between">
          <div className="min-w-0 flex-1">{leftComponent}</div>
          <div className="ml-10 flex flex-shrink-0 items-center space-x-10 pr-4">
            <div className="flex items-center space-x-8">
              <span className="inline-flex">{featureFlags.owner_rating_enabled && <NotificationsDrawer />}</span>

              {notification}

              <Menu as="div" className="relative inline-block text-left">
                <Menu.Button className="flex text-sm" onClick={closeUserMenuSubmenus}>
                  <span className="sr-only">Open user menu</span>
                  <div className="max-w-8xl flex items-center justify-center">
                    <div className="text-greyscale-60 flex justify-start text-sm font-normal leading-10">
                      <p
                        className={twMerge(
                          "text-primary-main w-10 rounded-full font-bold uppercase",
                          theme.myVesselsTable.tailwind.emptyStateIconBg,
                          theme.headerComponent.logInCircle.text,
                        )}
                      >
                        {firstNameFirstLetter + lastNameFirstLetter}
                      </p>
                    </div>

                    <div className="ml-3 flex min-w-0 flex-1 flex-col items-start">
                      <div className="text-primary-main truncate text-sm font-semibold text-green">
                        {user.name && formatName(user.name)}
                      </div>
                    </div>
                    <div className="ml-3">
                      <ChevronDownIcon className="h-5 w-5 text-green" />
                    </div>
                  </div>
                </Menu.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                  afterLeave={closeUserMenuSubmenus}
                >
                  <Menu.Items
                    className={twMerge(
                      "absolute right-0 z-30 mt-2 block w-56 origin-top-right rounded-xl shadow-lg focus:outline-none",
                      theme.headerComponent.profileMenu.background,
                    )}
                    style={profileMenuTextStyle}
                  >
                    <div className="relative p-4">
                      <button
                        type="button"
                        className={twMerge(profileMenuItemClassName, "hover:text-primary-main")}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setIsProfileMenuOpen((prev) => !prev);
                          setIsAppearanceMenuOpen(false);
                        }}
                      >
                        <span className="flex-1 text-left">Profile</span>
                        <ChevronDownIcon className="h-4 w-4 rotate-90" />
                      </button>

                      {isProfileMenuOpen && (
                        <div
                          className={twMerge(
                            "absolute right-full top-4 z-40 mr-2 w-64 rounded-xl p-4 shadow-lg",
                            theme.headerComponent.profileMenu.background,
                          )}
                          style={profileMenuTextStyle}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <p className="mb-1 text-sm font-semibold">User details</p>
                          <div className="mb-3 h-px w-16" style={{ backgroundColor: profileMenuTextColor }} />
                          <div className="space-y-3">
                            <div>
                              <p className={profileDetailLabelClassName}>Name</p>
                              <p className={profileDetailValueClassName}>{profileName}</p>
                            </div>
                            <div>
                              <p className={profileDetailLabelClassName}>Email</p>
                              <p className={profileDetailValueClassName}>{profileEmail}</p>
                            </div>
                            <div>
                              <p className={profileDetailLabelClassName}>Role(s)</p>
                              <p className={profileDetailValueClassName}>{formatProfileValue(profileRoleLabels)}</p>
                            </div>
                            <div>
                              <p className={profileDetailLabelClassName}>Access</p>
                              {profileAccessLabels.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {profileAccessLabels.map((accessLabel) => (
                                    <span
                                      key={accessLabel}
                                      className={profileAccessBadgeClassName}
                                      style={profileAccessBadgeStyle}
                                    >
                                      {accessLabel}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <p className={profileDetailValueClassName}>Not assigned</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      <button
                        type="button"
                        className={twMerge(profileMenuItemClassName, "hover:text-primary-main")}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setIsAppearanceMenuOpen((prev) => !prev);
                          setIsProfileMenuOpen(false);
                        }}
                      >
                        <span className="flex-1 text-left">Appearance</span>
                        <ChevronDownIcon className="h-4 w-4 rotate-90" />
                      </button>

                      {isAppearanceMenuOpen && (
                        <div
                          className={twMerge(
                            "absolute right-full top-12 z-40 mr-2 w-52 rounded-xl p-4 shadow-lg",
                            theme.headerComponent.profileMenu.background,
                          )}
                          style={profileMenuTextStyle}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <p className="mb-1 text-sm font-semibold">Theme</p>
                          <div className="mb-3 h-px w-10" style={{ backgroundColor: profileMenuTextColor }} />
                          <label className={themeOptionClassName}>
                            <input
                              type="radio"
                              name="appearance-theme"
                              value="bright"
                              className={themeRadioInputClassName}
                              checked={appearanceTheme === "bright"}
                              onChange={() => handleThemeSelect("bright")}
                            />
                            <span
                              aria-hidden="true"
                              className={themeRadioIndicatorClassName}
                              style={themeRadioBorderStyle}
                            >
                              {appearanceTheme === "bright" && (
                                <span className={themeRadioDotClassName} style={themeRadioDotStyle} />
                              )}
                            </span>
                            <span style={profileMenuTextStyle}>bright (day)</span>
                          </label>
                          <label className={themeOptionClassName}>
                            <input
                              type="radio"
                              name="appearance-theme"
                              value="night"
                              className={themeRadioInputClassName}
                              checked={appearanceTheme === "night"}
                              onChange={() => handleThemeSelect("night")}
                            />
                            <span
                              aria-hidden="true"
                              className={themeRadioIndicatorClassName}
                              style={themeRadioBorderStyle}
                            >
                              {appearanceTheme === "night" && (
                                <span className={themeRadioDotClassName} style={themeRadioDotStyle} />
                              )}
                            </span>
                            <span style={profileMenuTextStyle}>night (dark)</span>
                          </label>
                        </div>
                      )}

                      <Menu.Item>
                        {({ close }) => (
                          <button
                            type="button"
                            className={twMerge(profileMenuItemClassName, "hover:text-primary-main")}
                            onClick={() => {
                              setIsProfileMenuOpen(false);
                              setIsAppearanceMenuOpen(false);
                              setIsSupportContactModalOpen(true);
                              close();
                            }}
                          >
                            Contact support
                          </button>
                        )}
                      </Menu.Item>

                      {navigation.map(({ name, path, callback, icon, requiresConfirmation }) => (
                        <Menu.Item key={name}>
                          {({ close }) =>
                            requiresConfirmation && callback ? (
                              <button
                                type="button"
                                className={twMerge(profileMenuItemClassName, "hover:text-primary-main")}
                                onClick={() => {
                                  setIsProfileMenuOpen(false);
                                  setIsAppearanceMenuOpen(false);
                                  setLogoutConfirmationCallback(() => callback);
                                  close();
                                }}
                              >
                                {icon} {name}
                              </button>
                            ) : path ? (
                              <NavLink to={path} className={profileMenuLinkClassName} onClick={callback}>
                                <div className={profileMenuItemContentClassName}>
                                  {icon} {name}
                                </div>
                              </NavLink>
                            ) : (
                              <button
                                className={twMerge(profileMenuItemClassName, "hover:text-primary-main")}
                                onClick={callback}
                              >
                                {icon} {name}
                              </button>
                            )
                          }
                        </Menu.Item>
                      ))}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>
      </header>

      {isSupportContactModalOpen && <SupportContactModal onClose={() => setIsSupportContactModalOpen(false)} />}
      {logoutConfirmationCallback && (
        <LogoutConfirmationModal onCancel={closeLogoutConfirmation} onConfirm={confirmLogout} />
      )}
    </>
  );
};

export { CustomHeader };
